import {fromSeconds, parseDate} from 'common/date';
import type {
  FullCardBenefitFragment,
  FullFeePeriodFragment,
} from '@transactions/graphql';
import type {DateTime} from 'luxon';

/**
 * Window-state derivation for the benefits pages.
 *
 * Every visual on the redesigned pages reads from one five-state vocabulary,
 * with salience proportional to how much a window needs the user:
 * - captured: posted money, the quiet default
 * - booked: covered by linked transactions dated in the future (pre-generated
 *   credits, e.g. monthly Uber Cash) — counts toward totals but isn't in hand
 * - open: the window contains today and credit is still claimable — the only
 *   loud state
 * - missed/partial: the window expired with credit unused — locked in
 * - future: not open yet
 * plus 'over' (claims exceed the cap), reserved for data problems.
 *
 * Everything derives client-side from the fragments the pages already load;
 * "posted vs booked" splits usedAmount by comparing linked transaction dates
 * to now.
 */

type BenefitPeriodFragment = FullCardBenefitFragment['periods'][number];

export type WindowState =
  | 'captured'
  | 'booked'
  | 'open'
  | 'partial'
  | 'missed'
  | 'future'
  | 'over';

export type BenefitWindow = {
  startDate: number;
  endDate: number;
  amount: number;
  /** Signed net of linked transactions in the window (as stored) */
  usedAmount: number;
  /** usedAmount minus the future-dated portion — credit treated as in hand */
  postedAmount: number;
  /** Portion of usedAmount from future-dated transactions */
  bookedAmount: number;
  state: WindowState;
  /** Credit still claimable now; > 0 only when state is 'open' */
  openRemaining: number;
  /** Credit that expired unused; > 0 only for 'missed'/'partial' */
  missedAmount: number;
  /** Whole days until the window closes; null unless open */
  daysLeft: number | null;
};

export const deriveWindow = (
  period: BenefitPeriodFragment,
  now: DateTime,
): BenefitWindow => {
  const {startDate, endDate, amount, usedAmount} = period;
  // Booked = the explicitly future-dated portion; posted is the remainder.
  // Derived this way round so usage without a dated transaction behind it
  // counts as posted (in hand), never as scheduled.
  let bookedAmount = 0;
  for (const rt of period.rawTransactions) {
    if (parseDate(rt.date) > now) {
      bookedAmount += rt.amount;
    }
  }
  const postedAmount = usedAmount - bookedAmount;

  const expired = fromSeconds(endDate) <= now;
  const future = fromSeconds(startDate) > now;
  const full = amount > 0 && usedAmount >= amount;

  let state: WindowState;
  let openRemaining = 0;
  let missedAmount = 0;
  let daysLeft: number | null = null;

  if (amount > 0 && usedAmount > amount) {
    state = 'over';
  } else if (future) {
    // A future window already covered by pre-generated credits is booked
    state = full ? 'booked' : 'future';
  } else if (expired) {
    if (full) {
      state = 'captured';
    } else {
      // Floor usedAmount at 0 so net-negative windows (clawbacks exceeding
      // credits) forfeit at most the window's own amount
      missedAmount = Math.max(0, amount - Math.max(0, usedAmount));
      state = usedAmount > 0 ? 'partial' : 'missed';
    }
  } else if (full) {
    state = bookedAmount > 0 ? 'booked' : 'captured';
  } else {
    state = 'open';
    // Signed usedAmount, matching the server's remainingAmount resolver: a
    // net-negative window (reversal exceeding credits) has more than the cap
    // left to claim, not the cap
    openRemaining = Math.max(0, amount - usedAmount);
    daysLeft = Math.max(0, Math.floor(fromSeconds(endDate).diff(now, 'days').days));
  }

  return {
    startDate,
    endDate,
    amount,
    usedAmount,
    postedAmount,
    bookedAmount,
    state,
    openRemaining,
    missedAmount,
    daysLeft,
  };
};

export type BenefitSummary = {
  benefit: FullCardBenefitFragment;
  /** Windows in chronological order */
  windows: BenefitWindow[];
  totalAmount: number;
  totalUsed: number;
  posted: number;
  booked: number;
  /** Claimable right now across open windows */
  open: number;
  missed: number;
  /** Credit in windows that have not opened yet */
  future: number;
  hasOver: boolean;
  /** Earliest open window (nearest deadline) */
  nextOpenWindow: BenefitWindow | null;
  /** Earliest window that has not opened yet */
  nextFutureWindow: BenefitWindow | null;
};

export const summarizeBenefit = (
  benefit: FullCardBenefitFragment,
  now: DateTime,
): BenefitSummary => {
  const windows = [...benefit.periods]
    .sort((a, b) => a.startDate - b.startDate)
    .map((period) => deriveWindow(period, now));

  const summary: BenefitSummary = {
    benefit,
    windows,
    totalAmount: 0,
    totalUsed: 0,
    posted: 0,
    booked: 0,
    open: 0,
    missed: 0,
    future: 0,
    hasOver: false,
    nextOpenWindow: null,
    nextFutureWindow: null,
  };

  for (const window of windows) {
    summary.totalAmount += window.amount;
    summary.totalUsed += window.usedAmount;
    summary.posted += window.postedAmount;
    summary.booked += window.bookedAmount;
    summary.open += window.openRemaining;
    summary.missed += window.missedAmount;
    if (window.state === 'future') {
      // Only the unclaimed remainder: a partially pre-covered future window
      // already carries its usage in the captured totals, and a net-negative
      // one has more than its cap still ahead
      summary.future += Math.max(0, window.amount - window.usedAmount);
    }
    if (window.state === 'over') {
      summary.hasOver = true;
    }
    if (
      window.state === 'open' &&
      (summary.nextOpenWindow == null ||
        window.endDate < summary.nextOpenWindow.endDate)
    ) {
      // Nearest deadline, not first by start date — accepted custom windows
      // can overlap, so an earlier-starting window may close later
      summary.nextOpenWindow = window;
    }
    if (window.state === 'future' && summary.nextFutureWindow == null) {
      summary.nextFutureWindow = window;
    }
  }

  return summary;
};

export type FeeYearStats = {
  feePeriod: FullFeePeriodFragment;
  benefits: BenefitSummary[];
  isCurrent: boolean;
  fee: number;
  /** Sum of window caps — what the year could pay out at most */
  potential: number;
  /** Sum of used amounts (posted + booked), the app's "credits" number */
  captured: number;
  posted: number;
  booked: number;
  open: number;
  missed: number;
  future: number;
  /** captured − fee */
  net: number;
  openWindowCount: number;
  missedWindowCount: number;
  /** Sum of linked transaction amounts outside every window */
  uncovered: number;
};

export const summarizeFeeYear = (
  feePeriod: FullFeePeriodFragment,
  now: DateTime,
): FeeYearStats => {
  const stats: FeeYearStats = {
    feePeriod,
    benefits: feePeriod.benefits.map((benefit) =>
      summarizeBenefit(benefit, now),
    ),
    isCurrent:
      fromSeconds(feePeriod.startDate) <= now &&
      now < fromSeconds(feePeriod.endDate),
    fee: feePeriod.amount,
    potential: 0,
    captured: 0,
    posted: 0,
    booked: 0,
    open: 0,
    missed: 0,
    future: 0,
    net: 0,
    openWindowCount: 0,
    missedWindowCount: 0,
    uncovered: 0,
  };

  for (const summary of stats.benefits) {
    stats.potential += summary.totalAmount;
    stats.captured += summary.totalUsed;
    stats.posted += summary.posted;
    stats.booked += summary.booked;
    stats.open += summary.open;
    stats.missed += summary.missed;
    stats.future += summary.future;
    stats.uncovered += summary.benefit.uncoveredAmount;
    for (const window of summary.windows) {
      if (window.state === 'open') stats.openWindowCount++;
      if (window.missedAmount > 0) stats.missedWindowCount++;
    }
  }

  stats.net = stats.captured - stats.fee;
  return stats;
};

/** An open window across any card/benefit, for the claim queue */
export type ClaimQueueItem = {
  feePeriod: FullFeePeriodFragment;
  benefit: FullCardBenefitFragment;
  window: BenefitWindow;
};

/**
 * Open windows across the given fee periods, nearest deadline first (largest
 * remaining amount breaks ties).
 */
export const buildClaimQueue = (
  feePeriods: readonly FullFeePeriodFragment[],
  now: DateTime,
): ClaimQueueItem[] => {
  const items: ClaimQueueItem[] = [];
  for (const feePeriod of feePeriods) {
    for (const benefit of feePeriod.benefits) {
      for (const window of summarizeBenefit(benefit, now).windows) {
        if (window.state === 'open' && window.openRemaining > 0) {
          items.push({feePeriod, benefit, window});
        }
      }
    }
  }
  return items.sort(
    (a, b) =>
      a.window.endDate - b.window.endDate ||
      b.window.openRemaining - a.window.openRemaining,
  );
};

export type OpeningSoonItem = {
  benefit: FullCardBenefitFragment;
  window: BenefitWindow;
  /** What the window will actually offer: its cap less any pre-covered usage */
  remaining: number;
};

/**
 * The next not-yet-open window per benefit, soonest first, limited to windows
 * starting within `withinDays`. "Booked" future windows are excluded — they
 * need no action when they open.
 */
export const buildOpeningSoon = (
  feePeriods: readonly FullFeePeriodFragment[],
  now: DateTime,
  withinDays: number,
): OpeningSoonItem[] => {
  const horizon = now.plus({days: withinDays});
  const items: OpeningSoonItem[] = [];
  for (const feePeriod of feePeriods) {
    for (const benefit of feePeriod.benefits) {
      const {nextFutureWindow} = summarizeBenefit(benefit, now);
      if (
        nextFutureWindow != null &&
        fromSeconds(nextFutureWindow.startDate) <= horizon
      ) {
        items.push({
          benefit,
          window: nextFutureWindow,
          remaining: Math.max(
            0,
            nextFutureWindow.amount - nextFutureWindow.usedAmount,
          ),
        });
      }
    }
  }
  return items.sort((a, b) => a.window.startDate - b.window.startDate);
};

/**
 * Posted/booked widths for meters and bars, netted against the signed
 * captured total: a posted credit with a future-dated reversal produces
 * posted > captured and booked < 0, which raw widths would overdraw. The
 * posted span caps at captured and booked is the non-negative remainder.
 */
export const nettedSpans = (stats: {
  captured: number;
  posted: number;
}): {posted: number; booked: number} => {
  const capturedPos = Math.max(0, stats.captured);
  const posted = Math.max(0, Math.min(stats.posted, capturedPos));
  return {posted, booked: capturedPos - posted};
};

// ==================== Fee-year time axis ====================

export type FeeYearAxis = {
  /**
   * UTC midnight of the first day of the axis's starting month — the fee
   * year's start month, or earlier when a window lies entirely before it
   */
  start: DateTime;
  monthCount: number;
  /** Short uppercase month labels, one per column */
  labels: string[];
};

export const buildFeeYearAxis = (feePeriod: {
  startDate: number;
  endDate: number;
  benefits?: ReadonlyArray<{
    periods: ReadonlyArray<{startDate: number; endDate: number}>;
  }>;
}): FeeYearAxis => {
  // The axis must cover every window, not just the fee year: the editor
  // accepts custom windows anywhere, and an off-axis window would silently
  // vanish from the grid while still counting in totals. Windows that merely
  // *straddle* the fee-year start (the calendar snap-out case) keep the
  // left-clip fade — only a window lying entirely before the fee year
  // extends the axis leftward.
  const feeMonthStart = fromSeconds(feePeriod.startDate).startOf('month');
  let startSeconds = feePeriod.startDate;
  let endSeconds = feePeriod.endDate;
  for (const benefit of feePeriod.benefits ?? []) {
    for (const period of benefit.periods) {
      if (period.endDate > endSeconds) endSeconds = period.endDate;
      if (
        fromSeconds(period.endDate) <= feeMonthStart &&
        period.startDate < startSeconds
      ) {
        startSeconds = period.startDate;
      }
    }
  }
  const start = fromSeconds(startSeconds).startOf('month');
  const end = fromSeconds(endSeconds);
  const months = end.diff(start, 'months').months;
  // Ceil so a partial final month gets its own column (a Feb 9 anniversary
  // year spans Feb..Feb, 13 columns) — otherwise the closing days of the fee
  // year would render past the axis, clipped and without a today line. The
  // epsilon keeps exact month boundaries (Apr 1 – Apr 1) at 12. No upper
  // clamp: the server permits fee periods of any length, and the grid
  // scrolls horizontally rather than truncating.
  const monthCount = Math.max(1, Math.ceil(months - 0.001));
  const labels: string[] = [];
  for (let i = 0; i < monthCount; i++) {
    labels.push(
      start.plus({months: i}).toLocaleString({month: 'short'}).toUpperCase(),
    );
  }
  return {start, monthCount, labels};
};

/**
 * Fractional month offset of a timestamp from the axis start — the shared
 * coordinate every cell and the today line are positioned in. Can be
 * negative (calendar-aligned windows legitimately start before the fee year;
 * such cells render clipped at the axis edge).
 */
export const monthOffset = (axis: FeeYearAxis, seconds: number): number => {
  const dt = fromSeconds(seconds);
  return (
    (dt.year - axis.start.year) * 12 +
    (dt.month - axis.start.month) +
    (dt.day - 1) / dt.daysInMonth
  );
};

/**
 * monthOffset for an arbitrary DateTime (used for the today line).
 * Normalized to UTC first — the axis is UTC, and reading local calendar
 * fields near a UTC day/month boundary would misplace the marker.
 */
export const monthOffsetOf = (axis: FeeYearAxis, dt: DateTime): number => {
  const utc = dt.toUTC();
  return (
    (utc.year - axis.start.year) * 12 +
    (utc.month - axis.start.month) +
    (utc.day - 1) / utc.daysInMonth
  );
};
